import { useState, useEffect } from "react";
import { TbX, TbSearch } from "react-icons/tb";
import { toast } from "react-toastify";
import TextEdit from "../../../molecules/TextEdit";
import CurrencyInput from "../../../molecules/CurrencyInput";
import DateField from "../../../molecules/DateField";
import MaskEdit from "../../../organisms/MaskEdit";
import SelectTextField from "../../../molecules/SelectTextField";
import appConfig from "../../../../config/app-config";
import { Auth } from "aws-amplify";
import { PERSON_PRODUCTS, PERSON_PRODUCT_LABELS } from "../../../../constants/personProducts";
import { isProxySigner } from "./signatoryPayload";

const { address: PERSON_API_URL, key: PERSON_API_KEY } = appConfig.apis.personApiEnv;

interface SignatoryModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (signatory: SignatoryFormData) => void;
    editData?: SignatoryFormData | null;
    legalPersonUuid?: string;
}

export interface OperationWithFormat {
    operation: string;
    representationFormat: string;
}

export interface SignAsInfo {
    signAs: string;
    useDigitalCertificate: boolean;
}

export interface SignatoryFormData {
    id?: number;
    individualUuid?: string;
    fullName: string;
    socialId: string;
    position: string;
    signAsEntries: SignAsInfo[];
    signatureProfile: string;
    operationsWithFormat: OperationWithFormat[];
    email: string;
    mobilePhone: string;
    proxyExpirationDate?: string;
    proxyAllowedValue?: number | string;
}

const ALL_OPERATIONS = [...PERSON_PRODUCTS] as string[];
const REPRESENTATION_FORMATS = [
    { label: 'Isoladamente', value: 'ISOLATED' },
    { label: '2 Pessoas', value: 'TWO_PERSONS' },
    { label: '3 Pessoas', value: 'THREE_PERSONS' }
];

const defaultOperationsWithFormat: OperationWithFormat[] = ALL_OPERATIONS.map(op => ({
    operation: op,
    representationFormat: 'ISOLATED'
}));

const EMPTY_FORM: SignatoryFormData = {
    fullName: '',
    socialId: '',
    position: '',
    signAsEntries: [],
    signatureProfile: '',
    operationsWithFormat: defaultOperationsWithFormat,
    email: '',
    mobilePhone: '',
};

const SignatoryModal: React.FC<SignatoryModalProps> = ({ isOpen, onClose, onSave, editData, legalPersonUuid }) => {
    const [form, setForm] = useState<SignatoryFormData>({ ...EMPTY_FORM });
    const [missingFields, setMissingFields] = useState<string[]>([]);
    const [searchCpf, setSearchCpf] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [individualFound, setIndividualFound] = useState(false);

    useEffect(() => {
        if (editData) {
            setForm(editData);
            setSearchCpf(editData.socialId);
            setIndividualFound(true);
        } else {
            setForm({ ...EMPTY_FORM });
            setSearchCpf('');
            setIndividualFound(false);
        }
        setMissingFields([]);
    }, [editData, isOpen]);

    const handleSearchByCpf = async () => {
        const cpfClean = searchCpf.replace(/\D/g, '');
        if (cpfClean.length !== 11) {
            toast.error('CPF deve ter 11 dígitos');
            return;
        }

        setIsSearching(true);
        try {
            const session = await Auth.currentSession();
            const token = session.getIdToken().getJwtToken();
            const headers = {
                'x-api-key': PERSON_API_KEY,
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            };

            const searchResponse = await fetch(`${PERSON_API_URL}/individual/page-list?name=${cpfClean}&email=&mobile=&page=0&size=10&sort=id,DESC`, { headers });

            if (!searchResponse.ok) {
                toast.error('Pessoa física não encontrada. Verifique o CPF ou cadastre primeiro.');
                setIndividualFound(false);
                return;
            }

            const searchData = await searchResponse.json();
            const list = searchData.list || [];
            const data = list.find((p: any) => p.socialId?.replace(/\D/g, '') === cpfClean);
            if (!data) {
                toast.error('Pessoa física não encontrada. Verifique o CPF ou cadastre primeiro.');
                setIndividualFound(false);
                return;
            }

            // Verificar se já é signatário desta PJ
            if (legalPersonUuid && data.uuid) {
                const checkRes = await fetch(`${PERSON_API_URL}/legal-persons/${legalPersonUuid}/signatories/check/${data.uuid}`, { headers });
                if (checkRes.ok) {
                    const checkData = await checkRes.json();
                    if (checkData.exists) {
                        toast.warn('Este signatário já está vinculado a esta empresa.');
                        setIndividualFound(false);
                        return;
                    }
                }
            }

            // Extrair dados do profile SIGNATORY e sign_as
            const signatoryProfile = data.profiles?.find((p: any) => p.type === 'SIGNATORY');
            const signAsEntries: SignAsInfo[] = (signatoryProfile?.sign_as || []).map((s: any) => ({
                signAs: s.sign_as,
                useDigitalCertificate: s.use_digital_certificate || false,
            }));

            setForm(prev => ({
                ...prev,
                individualUuid: data.uuid,
                fullName: data.name || '',
                socialId: data.socialId || '',
                email: data.email || '',
                mobilePhone: data.mobile || '',
                signAsEntries,
                signatureProfile: signatoryProfile?.type || '',
            }));

            setIndividualFound(true);
            toast.success(`Signatário encontrado: ${data.name}`);
        } catch (error) {
            toast.error('Erro ao buscar pessoa física. Tente novamente.');
            setIndividualFound(false);
        } finally {
            setIsSearching(false);
        }
    };

    const handleOperationToggle = (operation: string) => {
        setForm(prev => {
            const exists = prev.operationsWithFormat.some(o => o.operation === operation);
            return {
                ...prev,
                operationsWithFormat: exists
                    ? prev.operationsWithFormat.filter(o => o.operation !== operation)
                    : [...prev.operationsWithFormat, { operation, representationFormat: 'ISOLATED' }]
            };
        });
    };

    const handleOperationFormatChange = (operation: string, format: string) => {
        setForm(prev => ({
            ...prev,
            operationsWithFormat: prev.operationsWithFormat.map(o =>
                o.operation === operation ? { ...o, representationFormat: format } : o
            )
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!individualFound) {
            toast.error('Busque um signatário pelo CPF antes de salvar');
            return;
        }

        // Validar campos que vêm do cadastro da PF (apenas na criação)
        if (!isEditing) {
            const cadastroMissing: { field: string; label: string }[] = [];
            if (!form.socialId?.replace(/\D/g, '')) cadastroMissing.push({ field: 'socialId', label: 'CPF' });
            if (!form.fullName?.trim()) cadastroMissing.push({ field: 'fullName', label: 'Nome' });
            if (!form.email?.trim()) cadastroMissing.push({ field: 'email', label: 'Email' });
            if (!form.mobilePhone?.replace(/\D/g, '')) cadastroMissing.push({ field: 'mobilePhone', label: 'Telefone Celular' });
            if (!form.signatureProfile?.trim()) cadastroMissing.push({ field: 'signatureProfile', label: 'Perfil de Assinatura' });
            if (!form.signAsEntries || form.signAsEntries.length === 0) cadastroMissing.push({ field: 'signAsEntries', label: 'Tipo' });

            if (cadastroMissing.length > 0) {
                const campos = cadastroMissing.map(m => m.label).join(', ');
                toast.error(`Cadastro incompleto. Campos em branco: ${campos}. Acesse o cadastro da pessoa física e preencha antes de vincular como signatário.`, { autoClose: 8000 });
                return;
            }
        }

        const missing: string[] = [];
        if (!form.position.trim()) missing.push('position');
        setMissingFields(missing);

        if (missing.length > 0) {
            toast.error('Preencha todos os campos obrigatórios');
            return;
        }

        try {
            await onSave(form);
            onClose();
        } catch {
            // erro tratado pelo caller (toast no pai)
        }
    };

    if (!isOpen) return null;

    const isEditing = !!editData;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="flex justify-between items-center p-6 border-b border-gray-200">
                    <h2 className="text-xl font-semibold text-gray-800">
                        {isEditing ? 'Editar Signatário' : 'Incluir Signatário'}
                    </h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
                        <TbX size={24} />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6">
                    {/* Busca por CPF */}
                    {!isEditing && (
                        <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Buscar Pessoa Física por CPF
                            </label>
                            <div className="flex gap-3">
                                <div className="flex-1">
                                    <MaskEdit
                                        id="searchCpf"
                                        label=""
                                        mask="___.___.___-__"
                                        replacement={{ '_': /\d/ }}
                                        value={searchCpf}
                                        onChange={(e) => setSearchCpf(e.target.value)}
                                    />
                                </div>
                                <button
                                    type="button"
                                    onClick={handleSearchByCpf}
                                    disabled={isSearching}
                                    className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-400 transition-colors flex items-center gap-2"
                                >
                                    <TbSearch size={18} />
                                    {isSearching ? 'Buscando...' : 'Buscar'}
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Dados do Signatário (read-only, preenchidos pela busca) */}
                    {individualFound && (
                        <div className="grid grid-cols-12 gap-4">
                            <div className="col-span-12">
                                <TextEdit id="fullName" label="Nome Completo" value={form.fullName} disabled onChange={() => { }} />
                            </div>

                            <div className="col-span-3">
                                <MaskEdit id="socialId" label="CPF" mask="___.___.___-__" replacement={{ '_': /\d/ }} value={form.socialId} disabled onChange={() => { }} />
                            </div>

                            <div className="col-span-9">
                                <TextEdit
                                    id="position"
                                    label="Cargo *"
                                    value={form.position}
                                    onChange={(e) => {
                                        setForm(prev => ({ ...prev, position: e.target.value }));
                                        setMissingFields(prev => prev.filter(f => f !== 'position'));
                                    }}
                                    className={missingFields.includes('position') ? 'border-red-500' : ''}
                                />
                            </div>

                            <div className="col-span-6">
                                <TextEdit id="email" label="Email" value={form.email} disabled onChange={() => { }} />
                            </div>

                            <div className="col-span-6">
                                <MaskEdit id="mobilePhone" label="Telefone Celular" mask="(__) _____-____" replacement={{ '_': /\d/ }} value={form.mobilePhone} disabled onChange={() => { }} />
                            </div>

                            <div className="col-span-4">
                                <label className="block text-sm font-medium text-gray-500 mb-1">Perfil de Assinatura</label>
                                <span className="text-sm text-gray-800">{form.signatureProfile || '—'}</span>
                            </div>

                            {/* Tipos (sign_as) */}
                            <div className="col-span-8">
                                <label className="block text-sm font-medium text-gray-500 mb-1">Tipo(s)</label>
                                <div className="flex flex-wrap gap-2">
                                    {form.signAsEntries.length > 0 ? form.signAsEntries.map((entry, idx) => (
                                        <span key={idx} className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg bg-blue-50 text-blue-700 border border-blue-200">
                                            {entry.signAs}
                                            {entry.useDigitalCertificate && (
                                                <span className="text-green-600 ml-1" title="Certificado Digital">🔐</span>
                                            )}
                                        </span>
                                    )) : <span className="text-sm text-gray-400">Nenhum tipo definido</span>}
                                </div>
                            </div>

                            {isProxySigner(form.signAsEntries) && (
                                <div className="col-span-6">
                                    <DateField
                                        id="proxyExpirationDate"
                                        label="Vencimento da Procuração"
                                        value={form.proxyExpirationDate || ''}
                                        onChange={(isoDate) => setForm(prev => ({ ...prev, proxyExpirationDate: isoDate }))}
                                    />
                                </div>
                            )}
                            <div className="col-span-6">
                                <CurrencyInput
                                    id="proxyAllowedValue"
                                    label="Valor Permitido (R$)"
                                    prefix="R$"
                                    fractionDigits={2}
                                    value={form.proxyAllowedValue || 0}
                                    onChange={(value) => setForm(prev => ({ ...prev, proxyAllowedValue: value }))}
                                />
                            </div>

                            {/* Operações Permitidas e Formato de Representação */}
                            <div className="col-span-12">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-3">
                                    Operações Permitidas e Formato de Representação
                                </label>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">Clique para ativar/desativar. Selecione o formato de representação para cada produto.</p>
                                <div className="flex flex-wrap gap-2">
                                    {ALL_OPERATIONS.map((operation) => {
                                        const opData = form.operationsWithFormat.find(o => o.operation === operation);
                                        const isSelected = !!opData;
                                        const label = PERSON_PRODUCT_LABELS[operation] || operation;
                                        return (
                                            <div key={operation} className="relative group">
                                                <button
                                                    type="button"
                                                    onClick={() => handleOperationToggle(operation)}
                                                    aria-pressed={isSelected}
                                                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all border ${isSelected
                                                        ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                                                        : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400 hover:text-blue-600 dark:bg-slate-800 dark:text-gray-300 dark:border-slate-600 dark:hover:border-blue-400'
                                                        }`}
                                                >
                                                    {isSelected && (
                                                        <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                        </svg>
                                                    )}
                                                    {label}
                                                </button>
                                                {isSelected && (
                                                    <div className="mt-1 flex justify-center">
                                                        <select
                                                            value={opData.representationFormat}
                                                            onChange={(e) => handleOperationFormatChange(operation, e.target.value)}
                                                            className="text-xs bg-blue-50 text-blue-700 border border-blue-200 rounded-full px-2.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-blue-400 appearance-none cursor-pointer dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-700"
                                                            style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 20 20\' fill=\'%232563eb\'%3E%3Cpath fill-rule=\'evenodd\' d=\'M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z\' clip-rule=\'evenodd\'/%3E%3C/svg%3E")', backgroundRepeat: 'no-repeat', backgroundPosition: 'right 6px center', backgroundSize: '14px', paddingRight: '22px' }}
                                                        >
                                                            {REPRESENTATION_FORMATS.map(fmt => (
                                                                <option key={fmt.value} value={fmt.value}>{fmt.label}</option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Actions */}
                    <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-gray-200">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-6 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 transition-colors"
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            disabled={!individualFound}
                            className="px-6 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-400 transition-colors"
                        >
                            {isEditing ? 'Salvar Alterações' : 'Adicionar Signatário'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default SignatoryModal;
